/**
 * OnboardingScreen — v5.1 · 2 telas educativas antes do Login.
 *
 * Tela 1 (T1) — Apresentação do Beraldo:
 *   Hero (Beraldo 120px + wordmark green-dim + tagline italic moss) +
 *   promessa editorial + note-card offline + CTA "Próximo →"
 *
 * Tela 2 (T2X) — Em 3 toques:
 *   Eyebrow emergência + título + 3 cards numerados + CTA "Estou pronto →"
 *
 * Decisão #16: fluxo Splash → T1 → T2 → Login.h
 * Link "Pular" disponível nas duas telas (acessibilidade emergencial — decisão #10).
 *
 * localStorage key: 'florasegura:onboarding-v5-done'
 *   (nova chave — todos os usuários verão o onboarding v5)
 *
 * v5.1 fixes (22/04/2026):
 *   - tokens hex → rgba v5.1
 *   - Inter body: fontWeight 300 → 400
 *   - CTA cor: #0A0E0A → #0B1010
 *   - cards/CTAs: borderRadius 14 → 12 (--r-card-calm)
 *   - ProgressDots: respeita prefers-reduced-motion
 *   - SkipButton: top 10 → 54 (abaixo da status bar)
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import PhoneFrame from '../components/PhoneFrame'

// ─── Chave de localStorage ────────────────────────────────────────────────────

const ONBOARDING_KEY = 'florasegura:onboarding-v5-done'

export function markOnboardingDone() {
  try { localStorage.setItem(ONBOARDING_KEY, '1') } catch { /* noop */ }
}

export function isOnboardingDone(): boolean {
  try { return localStorage.getItem(ONBOARDING_KEY) === '1' } catch { return false }
}

// ─── prefers-reduced-motion ───────────────────────────────────────────────────

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return reduced
}

// ─── Tokens v5.1 ─────────────────────────────────────────────────────────────

const V5 = {
  bg:        '#0B1010',
  primary:   '#A8CE64',
  primaryDim:'#7FA450',
  red:       '#D94040',
  amber:     '#E3B341',
  moss:      '#B8D4A8',
  clay:      '#D9C9A8',
  text:      'rgba(255,255,255,0.95)',
  text2:     'rgba(255,255,255,0.65)',
  text3:     'rgba(255,255,255,0.50)',
  ornament:  'rgba(255,255,255,0.50)',
  surface:   'rgba(255,255,255,0.025)',
  surface2:  'rgba(255,255,255,0.04)',
  border:    'rgba(255,255,255,0.06)',
  borderSt:  'rgba(255,255,255,0.10)',
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function OnboardingScreen() {
  const navigate = useNavigate()
  const [step, setStep] = useState(0)

  function handleSkip() {
    markOnboardingDone()
    navigate('/login', { replace: true })
  }

  function handleReady() {
    markOnboardingDone()
    navigate('/login', { replace: true })
  }

  return (
    <PhoneFrame>
      {step === 0 && (
        <StepApresentacao
          onNext={() => setStep(1)}
          onSkip={handleSkip}
        />
      )}
      {step === 1 && (
        <StepEmergencia
          onReady={handleReady}
          onBack={() => setStep(0)}
          onSkip={handleSkip}
        />
      )}
    </PhoneFrame>
  )
}

// ─── Botão Skip ───────────────────────────────────────────────────────────────

function SkipButton({ onSkip }: { onSkip: () => void }) {
  return (
    <button
      onClick={onSkip}
      style={{
        position: 'absolute', top: 54, right: 18, zIndex: 20,
        fontFamily: "var(--font-sans)", fontSize: 'var(--t-meta)',
        color: V5.text2, padding: '10px 14px',
        background: 'rgba(11,16,16,0.5)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderRadius: 'var(--r-pill-filter)', border: `1px solid ${V5.borderSt}`,
        minHeight: 44, display: 'inline-flex',
        alignItems: 'center', gap: 6, cursor: 'pointer',
      }}
    >
      Pular
    </button>
  )
}

// ─── Progress Dots ────────────────────────────────────────────────────────────

function ProgressDots({ active }: { active: 0 | 1 }) {
  const reduced = usePrefersReducedMotion()
  return (
    <div style={{
      display: 'flex', gap: 8, justifyContent: 'center',
      flexShrink: 0, padding: '12px 0 16px',
      pointerEvents: 'none',
    }}>
      {([0, 1] as const).map(i => (
        <span
          key={i}
          style={{
            display: 'block',
            width: i === active ? 24 : 6,
            height: 6,
            borderRadius: 'var(--r-pill-filter)',
            background: i === active ? V5.primary : 'rgba(168,175,168,0.25)',
            transition: reduced ? 'none' : 'all 0.25s ease',
          }}
        />
      ))}
    </div>
  )
}

// ─── Tela 1 — Apresentação do Beraldo ────────────────────────────────────────

function StepApresentacao({ onNext, onSkip }: { onNext: () => void; onSkip: () => void }) {
  return (
    <div style={{
      flex: 1, position: 'relative',
      display: 'flex', flexDirection: 'column',
      background: V5.bg, overflow: 'hidden',
    }}>
      <SkipButton onSkip={onSkip} />

      {/* Hero */}
      <div style={{
        flexShrink: 0, padding: '54px 28px 24px',
        textAlign: 'center', position: 'relative',
        background: 'linear-gradient(180deg, #0E1514 0%, #0B1010 100%)',
      }}>
        {/* Linha separadora verde no rodapé do hero */}
        <div style={{
          position: 'absolute', bottom: 0, left: '50%',
          transform: 'translateX(-50%)',
          width: 200, height: 1,
          background: 'linear-gradient(90deg, transparent 0%, rgba(168,206,100,0.25) 50%, transparent 100%)',
        }} />

        {/* Foto do Beraldo */}
        <img
          src="/models/modeloberaldofinal2.png"
          alt="Beraldo, o botânico"
          style={{
            width: 120, height: 120, borderRadius: '50%',
            objectFit: 'cover', margin: '0 auto 22px', display: 'block',
            boxShadow: '0 6px 28px rgba(168,206,100,0.18), 0 0 0 1px rgba(244,241,233,0.08)',
          }}
        />

        {/* Wordmark */}
        <div style={{
          fontFamily: "var(--font-brand)",
          fontSize: 'var(--t-display)', fontWeight: 500,
          fontVariationSettings: '"opsz" 144',
          letterSpacing: '-0.02em', lineHeight: 1.05,
          marginBottom: 6, color: V5.primaryDim,
        }}>
          Flora Segura
        </div>

        {/* Tagline */}
        <p style={{
          fontFamily: "var(--font-display)",
          fontStyle: 'italic', fontSize: 'var(--t-body)', fontWeight: 400,
          letterSpacing: '0.005em', color: V5.moss,
          margin: '0 auto',
        }}>
          com <strong style={{ fontWeight: 400, color: V5.moss }}>Beraldo</strong>, o botânico
        </p>
      </div>

      {/* Body scrollável */}
      <div style={{
        flex: 1, overflowY: 'auto',
        padding: '32px 32px 0',
        display: 'flex', flexDirection: 'column',
        paddingBottom: 24,
      }}>
        <h2 style={{
          fontFamily: "var(--font-display)", fontWeight: 400,
          fontSize: 'var(--t-hero)', lineHeight: 1.15, letterSpacing: '-0.018em', color: V5.text,
          marginBottom: 14,
        }}>
          Olá! Meu nome é{' '}
          <em style={{ fontStyle: 'italic', color: V5.moss, fontWeight: 400 }}>Beraldo</em>.
        </h2>

        <p style={{
          fontSize: 'var(--t-body)', lineHeight: 1.65,
          color: V5.text2, fontWeight: 400,
          marginBottom: 22, maxWidth: 300,
        }}>
          Identifico plantas tóxicas para o seu bichinho. Você fotografa, eu analiso,
          e devolvo um diagnóstico em segundos —{' '}
          <strong style={{ fontWeight: 500, color: V5.text }}>
            com cobertura para 7 espécies
          </strong>{' '}
          de animais.
        </p>

        {/* Note card — offline */}
        <div style={{
          background: 'rgba(168,206,100,0.05)',
          border: '1px solid rgba(168,206,100,0.2)',
          borderRadius: 'var(--r-card-calm)', padding: '12px 14px', marginBottom: 14,
          fontSize: 'var(--t-meta)', lineHeight: 1.55, color: V5.text2,
          display: 'flex', gap: 10, alignItems: 'flex-start',
        }}>
          <span style={{
            width: 20, height: 20, color: V5.primary,
            flexShrink: 0, marginTop: 1, display: 'flex',
          }}>
            <svg
              viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
              style={{ width: '100%', height: '100%' }}
            >
              <path d="M12 22s8-4 8-12V5l-8-3-8 3v5c0 8 8 12 8 12z" />
            </svg>
          </span>
          <span>
            <strong style={{ color: V5.moss, fontWeight: 500 }}>Funciona offline</strong> para o
            herbário e cadastro. Identificação em tempo real precisa de conexão.
          </span>
        </div>

        {/* CTA */}
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 20 }}>
          <button
            onClick={onNext}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: 52, borderRadius: 'var(--r-card-calm)',
              fontFamily: "var(--font-sans)", fontSize: 'var(--t-base)', fontWeight: 700,
              letterSpacing: '-0.005em', cursor: 'pointer',
              width: '100%', border: '1px solid var(--v5-primary)',
              background: 'var(--fs-btn-bg)', color: 'var(--v5-primary)',
              boxShadow: 'var(--v5-card-shadow)',
            }}
          >
            Próximo →
          </button>
        </div>
      </div>

      <ProgressDots active={0} />
    </div>
  )
}

// ─── Tela 2 — Em 3 toques ────────────────────────────────────────────────────

const STEPS_DATA = [
  {
    num: '1',
    icon: (
      <svg
        viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: '100%', height: '100%' }}
      >
        <path d="M3 7h3l2-3h8l2 3h3v13H3z" />
        <circle cx="12" cy="13" r="4" />
      </svg>
    ),
    title: 'Fotografe a planta',
    desc: 'Câmera abre direto. Aproxime de uma folha visível.',
  },
  {
    num: '2',
    icon: (
      <svg
        viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: '100%', height: '100%' }}
      >
        <path d="M5 15c0-3 3-5 7-5s7 2 7 5v3H5z" />
        <circle cx="12" cy="7" r="3" />
      </svg>
    ),
    title: 'Escolha o animal',
    desc: '7 espécies: cão, gato, ave, equino, bovino, ovino, suíno.',
  },
  {
    num: '3',
    icon: (
      <svg
        viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: '100%', height: '100%' }}
      >
        <path d="M5 12l4 4 10-10" />
      </svg>
    ),
    title: 'Receba o diagnóstico',
    desc: 'Seguro, Cuidado ou Tóxico — com nuance e ações.',
  },
]

function StepEmergencia({
  onReady,
  onBack,
  onSkip,
}: {
  onReady: () => void
  onBack: () => void
  onSkip: () => void
}) {
  return (
    <div style={{
      flex: 1, position: 'relative',
      display: 'flex', flexDirection: 'column',
      background: V5.bg, overflow: 'hidden',
    }}>
      <SkipButton onSkip={onSkip} />

      {/* Intro T2 */}
      <div style={{ flexShrink: 0, padding: '54px 32px 0', textAlign: 'left' }}>
        {/* Eyebrow */}
        <div style={{
          fontFamily: "var(--font-sans)",
          fontSize: 'var(--t-meta)', fontWeight: 500,
          letterSpacing: '0.22em', color: V5.primary,
          textTransform: 'uppercase', marginBottom: 14,
          display: 'inline-flex', alignItems: 'center', gap: 8,
        }}>
          <svg
            viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
            style={{ width: 14, height: 14, flexShrink: 0 }}
          >
            <path d="M12 2L3 9l1.5 12h15L21 9z" />
          </svg>
          Em caso de emergência
        </div>

        {/* Título */}
        <h2 style={{
          fontFamily: "var(--font-display)", fontWeight: 400,
          fontSize: 'var(--t-hero)', lineHeight: 1.15, letterSpacing: '-0.018em', color: V5.text,
          marginBottom: 8,
        }}>
          Em <em style={{ fontStyle: 'italic', color: V5.moss, fontWeight: 400 }}>3 toques.</em>
        </h2>

        {/* Subtítulo */}
        <p style={{
          fontSize: 'var(--t-body)', lineHeight: 1.65,
          color: V5.text2, fontWeight: 400, marginBottom: 6,
        }}>
          Sem cadastro, sem login, sem espera — o fluxo crítico está sempre a um toque do{' '}
          <strong style={{ fontWeight: 500, color: V5.text }}>SOS Pet</strong>.
        </p>
      </div>

      {/* Body scrollável */}
      <div style={{
        flex: 1, overflowY: 'auto',
        padding: '8px 32px 0',
        display: 'flex', flexDirection: 'column',
        paddingBottom: 24,
      }}>
        {/* Cards de passos */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 8, marginBottom: 18 }}>
          {STEPS_DATA.map(s => (
            <div key={s.num} style={{
              display: 'flex', gap: 14, alignItems: 'center',
              padding: 14,
              background: V5.surface2,
              border: `1px solid ${V5.border}`,
              borderRadius: 'var(--r-card-calm)',
            }}>
              {/* Número — medalhão único 40px (UX 2026) */}
              <span style={{
                width: 40, height: 40, borderRadius: 'var(--r-pill-filter)', flexShrink: 0,
                background: 'rgba(168,206,100,0.1)',
                border: '1px solid rgba(168,206,100,0.3)',
                color: V5.primary,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: "var(--font-display)",
                fontSize: 18, fontWeight: 500,
              }}>
                {s.num}
              </span>

              {/* Texto */}
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontFamily: "var(--font-display)", fontWeight: 400,
                  fontSize: 'var(--t-body)', lineHeight: 1.3, color: V5.text, marginBottom: 3,
                }}>
                  {s.title}
                </h3>
                <p style={{ fontSize: 'var(--t-meta)', lineHeight: 1.55, color: V5.text3 }}>
                  {s.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 20 }}>
          <button
            onClick={onReady}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: 52, borderRadius: 'var(--r-card-calm)',
              fontFamily: "var(--font-sans)", fontSize: 'var(--t-base)', fontWeight: 700,
              letterSpacing: '-0.005em', cursor: 'pointer',
              width: '100%', border: '1px solid var(--v5-primary)',
              background: 'var(--fs-btn-bg)', color: 'var(--v5-primary)',
              boxShadow: 'var(--v5-card-shadow)',
            }}
          >
            Estou pronto →
          </button>
          <button
            onClick={onBack}
            style={{
              background: 'transparent', color: V5.text2,
              fontSize: 'var(--t-body)', padding: '12px 0',
              minHeight: 44, width: '100%', border: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontFamily: "var(--font-sans)",
            }}
          >
            ← Voltar à apresentação
          </button>
        </div>
      </div>

      <ProgressDots active={1} />
    </div>
  )
}
